(() => {
  const rootPath = () => window.Shopify?.routes?.root || '/';

  function money(cents, format) {
    const value = (Number(cents) / 100).toFixed(2).replace('.', ',');
    if (format && format.includes('{{amount}}')) {
      return format.replace('{{amount}}', value).replace('{{amount_with_comma_separator}}', value);
    }
    return `R$ ${value}`;
  }

  function findVariant(product, selectedOptions) {
    return product.variants.find((variant) =>
      variant.options.every((option, index) => option === selectedOptions[index])
    );
  }

  function findClosestVariant(product, selectedOptions) {
    return (
      findVariant(product, selectedOptions) ||
      product.variants.find((variant) => variant.options[0] === selectedOptions[0]) ||
      product.variants[0]
    );
  }

  function cepDigits(value) {
    return String(value || '').replace(/\D/g, '').slice(0, 8);
  }

  function maskCep(value) {
    const digits = cepDigits(value);
    return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
  }

  function initPdp(root) {
    if (root.dataset.cdgPdpReady === 'true') return;
    root.dataset.cdgPdpReady = 'true';

    const jsonEl = root.querySelector('[data-cdg-product-json]');
    if (!jsonEl) return;

    let product;
    try {
      product = JSON.parse(jsonEl.textContent);
    } catch (error) {
      console.error('CDG PDP: invalid product JSON', error);
      return;
    }

    const form = root.querySelector('[data-cdg-product-form]');
    const variantInput = root.querySelector('[data-cdg-variant-id]');
    const qtyInput = root.querySelector('[data-cdg-qty-input]');
    const priceEls = [...root.querySelectorAll('[data-cdg-price]')];
    const compareEl = root.querySelector('[data-cdg-compare]');
    const installmentEl = root.querySelector('[data-cdg-installment]');
    const pixEls = [...root.querySelectorAll('[data-cdg-pix]')];
    const stockEl = root.querySelector('[data-cdg-stock]');
    const addButtons = [...root.querySelectorAll('[data-cdg-add]')];
    const buyNowBtn = root.querySelector('[data-cdg-buy-now]');
    const mainImage = root.querySelector('[data-cdg-main-image]');
    const thumbs = [...root.querySelectorAll('[data-cdg-thumb]')];
    const optionInputs = [...root.querySelectorAll('[data-cdg-option-input]')];
    const moneyFormat = window.theme?.moneyFormat || 'R$ {{amount_with_comma_separator}}';
    const installmentCount = Number(installmentEl?.dataset.installmentCount || 3);
    const pixPercent = Number(pixEls[0]?.dataset.pixPercent || 10);
    const cepInput = root.querySelector('[data-cdg-cep]');
    const cepSubmit = root.querySelector('[data-cdg-cep-submit]');
    const cepStatus = root.querySelector('[data-cdg-cep-status]');
    const cepRates = root.querySelector('[data-cdg-cep-rates]');

    const mediaById = {};
    (product.media || []).forEach((media) => {
      mediaById[String(media.id)] = media;
    });

    function selectedOptions() {
      const options = [];
      root.querySelectorAll('[data-cdg-option]').forEach((fieldset) => {
        const checked = fieldset.querySelector('[data-cdg-option-input]:checked');
        options.push(checked ? checked.value : '');
      });
      return options;
    }

    function quantity() {
      const value = Math.max(1, Number(qtyInput?.value || 1));
      if (qtyInput) qtyInput.value = String(value);
      return value;
    }

    function setActiveThumb(mediaId) {
      thumbs.forEach((thumb) => {
        const active = thumb.dataset.mediaId === String(mediaId);
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-pressed', String(active));
      });
    }

    function updateMainImage(media) {
      if (!mainImage || !media) return;
      const src = media.preview_image?.src || media.src;
      if (!src) return;
      const url = src.includes('?') ? `${src}&width=1400` : `${src}?width=1400`;
      mainImage.src = url;
      mainImage.srcset = '';
      setActiveThumb(media.id);
    }

    function updateUnavailableOptions() {
      const current = selectedOptions();
      optionInputs.forEach((input) => {
        const fieldset = input.closest('[data-cdg-option]');
        const index = Number(fieldset?.dataset.optionIndex || 0);
        const trial = current.slice();
        trial[index] = input.value;
        const match = findVariant(product, trial);
        const unavailable = !match || !match.available;
        input.disabled = Boolean(match) === false;
        const label = input.closest('label');
        if (label) label.classList.toggle('is-unavailable', unavailable);
      });
    }

    function updateUi(variant) {
      if (!variant) return;
      if (variantInput) variantInput.value = variant.id;
      priceEls.forEach((priceEl) => {
        priceEl.textContent = money(variant.price, moneyFormat);
      });

      if (compareEl) {
        if (variant.compare_at_price > variant.price) {
          compareEl.hidden = false;
          compareEl.textContent = money(variant.compare_at_price, moneyFormat);
        } else {
          compareEl.hidden = true;
        }
      }

      if (installmentEl) {
        const installment = Math.round(variant.price / installmentCount);
        installmentEl.textContent = `ou ${installmentCount}x de ${money(installment, moneyFormat)} sem juros no cartão`;
      }

      pixEls.forEach((pixEl) => {
        if (pixPercent <= 0) {
          pixEl.hidden = true;
          return;
        }
        const pixPrice = Math.round((variant.price * (100 - pixPercent)) / 100);
        pixEl.hidden = false;
        const compact = pixEl.classList.contains('cdg-pdp__buybar-pix');
        pixEl.textContent = compact
          ? `${money(pixPrice, moneyFormat)} no PIX`
          : `ou ${money(pixPrice, moneyFormat)} no PIX (${pixPercent}% off)`;
      });

      if (stockEl) {
        const available = Boolean(variant.available);
        stockEl.classList.toggle('is-oos', !available);
        stockEl.textContent = available ? 'Em estoque | pronta entrega' : 'Indisponível no momento';
      }

      [...addButtons, buyNowBtn].forEach((button) => {
        if (!button) return;
        button.disabled = !variant.available;
      });

      optionInputs.forEach((input) => {
        const fieldset = input.closest('[data-cdg-option]');
        const index = Number(fieldset?.dataset.optionIndex || 0);
        input.checked = variant.options[index] === input.value;
        const label = input.closest('label');
        if (label) label.classList.toggle('is-active', input.checked);
      });

      root.querySelectorAll('[data-cdg-option]').forEach((fieldset) => {
        const checked = fieldset.querySelector('[data-cdg-option-input]:checked');
        const valueEl = fieldset.querySelector('[data-cdg-option-value]');
        if (checked && valueEl) valueEl.textContent = checked.value;
      });

      updateUnavailableOptions();

      const media =
        (variant.featured_media && mediaById[String(variant.featured_media.id)]) ||
        (variant.featured_image && product.media?.find((item) => item.src === variant.featured_image.src)) ||
        product.media?.[0];
      if (media) updateMainImage(media);
    }

    optionInputs.forEach((input) => {
      input.addEventListener('change', () => {
        const variant = findClosestVariant(product, selectedOptions());
        updateUi(variant);
      });
    });

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const media = mediaById[thumb.dataset.mediaId];
        if (media) updateMainImage(media);
      });
    });

    root.querySelector('[data-cdg-qty-minus]')?.addEventListener('click', () => {
      if (qtyInput) qtyInput.value = String(Math.max(1, quantity() - 1));
    });
    root.querySelector('[data-cdg-qty-plus]')?.addEventListener('click', () => {
      if (qtyInput) qtyInput.value = String(quantity() + 1);
    });
    qtyInput?.addEventListener('change', quantity);

    cepInput?.addEventListener('input', () => {
      cepInput.value = maskCep(cepInput.value);
    });

    function setCepStatus(message, isError) {
      if (!cepStatus) return;
      cepStatus.hidden = !message;
      cepStatus.textContent = message || '';
      cepStatus.classList.toggle('is-error', Boolean(isError));
    }

    function deliveryText(rate) {
      const days = rate.delivery_days;
      if (Array.isArray(days) && days.length) {
        const min = days[0];
        const max = days[days.length - 1];
        return min === max ? `${min} dias úteis` : `${min} a ${max} dias úteis`;
      }
      return rate.delivery_range ? String(rate.delivery_range) : '';
    }

    function renderRates(rates, place) {
      if (!cepRates) return;
      cepRates.hidden = rates.length === 0;
      cepRates.replaceChildren();
      rates.forEach((rate) => {
        const item = document.createElement('li');
        item.className = 'cdg-pdp__shipping-rate';
        const copy = document.createElement('div');
        const name = document.createElement('strong');
        name.textContent = rate.presentment_name || rate.name || 'Frete';
        copy.append(name);
        const days = deliveryText(rate);
        if (days) {
          const prazo = document.createElement('span');
          prazo.textContent = days;
          copy.append(prazo);
        }
        const price = document.createElement('b');
        price.textContent = Number(rate.price) === 0
          ? 'Grátis'
          : money(Math.round(Number(rate.price) * 100), moneyFormat);
        item.append(copy, price);
        cepRates.append(item);
      });
      if (place) setCepStatus(place, false);
    }

    async function fetchRates(zip, uf) {
      const params = new URLSearchParams({
        'shipping_address[zip]': zip,
        'shipping_address[country]': 'Brazil'
      });
      if (uf) params.set('shipping_address[province]', uf);
      const response = await fetch(`${rootPath()}cart/shipping_rates.json?${params}`);
      if (!response.ok) return [];
      const data = await response.json();
      return data.shipping_rates || [];
    }

    async function quoteShipping() {
      const zip = cepDigits(cepInput?.value);
      if (zip.length !== 8) {
        setCepStatus('Digite um CEP válido com 8 dígitos.', true);
        return;
      }

      setCepStatus('Consultando CEP…', false);
      if (cepSubmit) cepSubmit.disabled = true;
      if (cepRates) {
        cepRates.hidden = true;
        cepRates.replaceChildren();
      }

      let place = '';
      let uf = '';
      let addedId = null;
      try {
        const via = await fetch(`https://viacep.com.br/ws/${zip}/json/`).then((response) => response.json());
        if (via.erro) {
          setCepStatus('CEP não encontrado.', true);
          if (cepSubmit) cepSubmit.disabled = false;
          return;
        }
        place = [via.localidade, via.uf].filter(Boolean).join('/');
        uf = via.uf || '';
      } catch (error) {
        console.warn('CDG PDP: ViaCEP indisponível', error);
      }

      try {
        let rates = await fetchRates(zip, uf);
        if (!rates.length && variantInput) {
          const cart = await fetch(`${rootPath()}cart.js`, { headers: { Accept: 'application/json' } }).then((response) => response.json());
          if (!cart.item_count) {
            const added = await fetch(`${rootPath()}cart/add.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: Number(variantInput.value), quantity: quantity() })
            });
            if (added.ok) {
              addedId = variantInput.value;
              rates = await fetchRates(zip, uf);
            }
          }
        }
        if (rates.length) {
          renderRates(rates, place ? `Entrega para ${place}` : '');
          return;
        }
      } catch (error) {
        console.warn('CDG PDP: shipping_rates indisponível', error);
      } finally {
        if (cepSubmit) cepSubmit.disabled = false;
        if (addedId) {
          await fetch(`${rootPath()}cart/change.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: addedId, quantity: 0 })
          }).catch(() => {});
        }
      }

      setCepStatus(
        `${place ? place + ': ' : ''}frete e prazo confirmados no checkout.`,
        false
      );
    }

    cepSubmit?.addEventListener('click', quoteShipping);
    cepInput?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        quoteShipping();
      }
    });

    async function addToCart(goCheckout) {
      if (!variantInput) return;
      const formData = new FormData(form);
      formData.set('id', variantInput.value);
      formData.set('quantity', String(quantity()));

      const response = await fetch(`${rootPath()}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData
      });

      if (!response.ok) throw new Error('Falha ao adicionar ao carrinho');

      const variant = product.variants.find((item) => String(item.id) === String(variantInput.value)) || product.variants[0];
      const qty = quantity();
      const payload = {
        currency: 'BRL',
        value: Number(((variant?.price || 0) / 100 * qty).toFixed(2)),
        items: window.cdgTrackItem ? [window.cdgTrackItem(product, variant, qty)] : []
      };
      window.cdgTrack?.('add_to_cart', payload);
      if (goCheckout) window.cdgTrack?.('begin_checkout', payload);

      if (goCheckout) {
        window.location.href = `${rootPath()}checkout`;
        return;
      }

      window.location.href = `${rootPath()}cart`;
    }

    form?.addEventListener('submit', (event) => {
      event.preventDefault();
      const buyNow = event.submitter?.hasAttribute('data-cdg-buy-now');
      addToCart(buyNow).catch((error) => {
        console.error(error);
        form.submit();
      });
    });

    const initial = product.variants.find((variant) => String(variant.id) === String(variantInput?.value)) || product.variants[0];
    updateUi(initial);
  }

  document.querySelectorAll('[data-cdg-pdp]').forEach(initPdp);
})();
